import React, { useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { bool, string } from "prop-types";

import Typography from "../shared/Typography";
import RequestButton from "../shared/RequestButton";
import { Button } from "@components/shared/Button";
import gtm from "@gtm/core";

import { setIsDealSavedByUserAction } from "@redux/actions/dealActions";
import { postDealAction } from "@redux/actions/dealActions";
import { CTA_SOL_TYPES, DEAL_TYPE } from "@shared/constants";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { saveProgressEvent } from "@gtm/events/shared";
import { getGTMPathnameStep } from "@gtm/core/utils";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { ROUTES } from "@shared/routing/routes";

import { useDealSelector } from "@redux/reducers/deal";

const SaveProgress = ({ updateSource }) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const { lastCta, isSavedByUser } = useDealSelector();
  const isUserTrusted = useIsUserTrustedSelector();
  const gtmStep = getGTMPathnameStep(router.pathname, lastCta);
  const routingUtils = useRoutingUtils();

  const [isSaving, setIsSaving] = useState(false);

  const triggerRequest = async () => {
    setIsSaving(true);
    dispatch(setIsDealSavedByUserAction(true));

    if (!isUserTrusted) {
      gtm.fire(saveProgressEvent(gtmStep));
      router.push(
        ROUTES.MY_ACCOUNT_LOGIN,
        routingUtils.getLocalizedPathname(
          ROUTES.MY_ACCOUNT_LOGIN,
          null,
          i18n.language
        )
      );

      return false;
    }

    await dispatch(
      postDealAction(
        CTA_SOL_TYPES.CONFIGURATION_SAVED_BY_USER,
        DEAL_TYPE.DEAL,
        null,
        true,
        updateSource
      )
    );

    setIsSaving(false);
  };

  const isDealSavedByUser = isSavedByUser && isUserTrusted && !isSaving;

  return (
    <div className="flex flex-col items-center justify-center gap-4 h-40 p-8">
      {isDealSavedByUser ? (
        <>
          <Typography className="text-sm">
            {t("footer.saveConfirmation")}
          </Typography>
          <Button
            size="small"
            variant="secondary"
            onClick={() =>
              router.push(
                ROUTES.MY_ACCOUNT_DEALS,
                routingUtils.getLocalizedPathname(
                  ROUTES.MY_ACCOUNT_DEALS,
                  null,
                  i18n.language
                )
              )
            }
          >
            {t("footer.viewSavedProgress")}
          </Button>
        </>
      ) : (
        <>
          <Typography className="text-sm">{t("footer.saveCarCTA")}</Typography>
          <RequestButton
            isLoading={isSaving}
            triggerRequestCallback={triggerRequest}
            onRequestSuccess={() => gtm.fire(saveProgressEvent(gtmStep))}
            onRequestError={console.error}
          >
            {t("footer.saveCarButton")}
          </RequestButton>
        </>
      )}
    </div>
  );
};

SaveProgress.propTypes = {
  isUserLoggedIn: bool,
  updateSource: string,
};

export default SaveProgress;
